// src/components/toolkit/InterviewKit.jsx — goes with Module 2.
//
// Builds the sheet you actually take to the interview: who it is, the
// questions you picked, the module's GENTLE steps, and what to do when it
// wanders. Everything here is the module's own material.
import { useState } from 'react';
import { ArrowLeft, Check, Copy, Mic } from 'lucide-react';
import { CONVERSATION_STARTERS, GENTLE_METHOD, NAVIGATION_TIPS } from '../../data/toolkitData';
import { loadTool, saveTool } from './toolStorage';

const TOOL_ID = 'interview-kit';
const card = 'rounded-xl border border-thread-border bg-white/5 p-5';

const THEME_LABELS = {
  timeAndPlace: 'Time and place',
  changeAndAdaptation: 'Change',
  personalJourney: 'Their own journey',
  familyConnections: 'Family',
};

const SITUATION_LABELS = {
  ifTheyResist: 'If they say no, or go quiet',
  ifTheyRamble: 'If they wander off the point',
  ifTheyGetEmotional: 'If they get upset',
  ifTheyForgetDetails: 'If they cannot remember',
};

const InterviewKit = ({ onBack, onComplete }) => {
  const [state, setState] = useState(() => loadTool(TOOL_ID, { who: '', picked: [], own: [], done: false }));
  const [draft, setDraft] = useState('');
  const [copied, setCopied] = useState(false);

  const update = (next) => {
    const merged = { ...state, ...next };
    setState(merged);
    saveTool(TOOL_ID, merged);
  };

  const toggle = (question) => {
    const picked = state.picked || [];
    update({ picked: picked.includes(question) ? picked.filter((q) => q !== question) : [...picked, question] });
  };

  const addOwn = () => {
    const question = draft.trim();
    if (!question) return;
    update({ own: [...(state.own || []), question] });
    setDraft('');
  };

  const questions = [...(state.picked || []), ...(state.own || [])];

  const sheetText = [
    state.who ? `Interview with ${state.who}` : 'Interview',
    '',
    ...questions.map((q, i) => `${i + 1}. ${q}`),
    '',
    'Before you start: ask if they are happy to be recorded, and say what you will do with it.',
    'While you talk: prop the phone up, start it and leave it alone. Ask, wait, then "Tell me more".',
    'To finish: "What should I have asked you that I didn\'t?"',
  ].join('\n');

  const copySheet = async () => {
    try {
      await navigator.clipboard.writeText(sheetText);
      setCopied(true);
      setTimeout(() => setCopied(false), 2500);
    } catch {
      setCopied(false);
    }
  };

  const finish = () => {
    update({ done: true });
    onComplete?.({ who: state.who, questionCount: questions.length });
  };

  return (
    <div className="min-h-screen bg-thread-darker text-white pt-36 pb-20">
      <div className="container mx-auto px-4 max-w-3xl">
        <button
          type="button"
          onClick={onBack}
          className="inline-flex items-center gap-2 min-h-[44px] text-thread-sand hover:text-white mb-6"
        >
          <ArrowLeft className="w-4 h-4" aria-hidden="true" /> Back to the toolkit
        </button>

        <h1 className="text-3xl md:text-4xl font-bold font-display mb-3">Interview kit</h1>
        <p className="text-slate-300 mb-8">
          Pick one person and a handful of questions. Ten good minutes recorded on a phone beats an hour you meant to do.
        </p>

        <label className="block mb-8">
          <span className="block font-semibold text-white mb-2">Who are you asking?</span>
          <input
            type="text"
            value={state.who || ''}
            onChange={(e) => update({ who: e.target.value })}
            placeholder="Nan, Uncle Sam, Mum..."
            className="w-full rounded-xl border border-white/20 bg-white/5 px-4 py-3 text-white placeholder:text-slate-500 focus:border-thread-glow focus:outline-none"
          />
        </label>

        <h2 className="text-2xl font-bold mb-3">Choose your questions</h2>
        <p className="text-slate-400 mb-4">Anything in square brackets is yours to fill in.</p>
        <div className="space-y-5 mb-6">
          {CONVERSATION_STARTERS.map((theme) => (
            <div key={theme.key} className={card}>
              <h3 className="font-semibold text-thread-sand mb-2">{THEME_LABELS[theme.key] || theme.key}</h3>
              <ul className="space-y-2">
                {theme.questions.map((question) => {
                  const active = (state.picked || []).includes(question);
                  return (
                    <li key={question}>
                      <button
                        type="button"
                        onClick={() => toggle(question)}
                        aria-pressed={active}
                        className="flex items-start gap-3 text-left w-full"
                      >
                        <span
                          className={`mt-0.5 w-5 h-5 shrink-0 rounded border flex items-center justify-center ${
                            active ? 'border-thread-glow bg-thread-ember text-white' : 'border-white/30'
                          }`}
                          aria-hidden="true"
                        >
                          {active && <Check className="w-3.5 h-3.5" />}
                        </span>
                        <span className={active ? 'text-white' : 'text-slate-300'}>{question}</span>
                      </button>
                    </li>
                  );
                })}
              </ul>
            </div>
          ))}
        </div>

        <div className="flex gap-2 mb-10">
          <input
            type="text"
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                addOwn();
              }
            }}
            placeholder="Add a question of your own"
            className="flex-1 rounded-xl border border-white/20 bg-white/5 px-4 py-3 text-white placeholder:text-slate-500 focus:border-thread-glow focus:outline-none"
          />
          <button
            type="button"
            onClick={addOwn}
            className="px-4 py-3 rounded-xl border border-white/25 hover:bg-white/10 text-white font-semibold"
          >
            Add
          </button>
        </div>

        <h2 className="text-2xl font-bold mb-3">Your sheet</h2>
        {questions.length === 0 ? (
          <p className="text-slate-400 mb-8">Nothing picked yet. Three or four questions is plenty.</p>
        ) : (
          <div className={`${card} mb-8`}>
            <p className="text-thread-sand mb-3">{state.who ? `For ${state.who}` : 'For your interview'}</p>
            <ol className="list-decimal list-inside text-slate-200 space-y-1 mb-4">
              {questions.map((question) => (
                <li key={question}>{question}</li>
              ))}
            </ol>
            <div className="flex flex-wrap gap-2">
              <button
                type="button"
                onClick={copySheet}
                className="inline-flex items-center gap-2 min-h-[44px] px-4 py-2 rounded-xl border border-white/25 hover:bg-white/10 text-white font-semibold"
              >
                <Copy className="w-4 h-4" aria-hidden="true" /> {copied ? 'Copied' : 'Copy the sheet'}
              </button>
              <button
                type="button"
                onClick={finish}
                className="inline-flex items-center gap-2 min-h-[44px] px-4 py-2 rounded-xl bg-thread-ember hover:bg-thread-rust text-white font-semibold transition-colors"
              >
                <Mic className="w-4 h-4" aria-hidden="true" /> {state.done ? 'Marked as done' : 'I did the interview'}
              </button>
            </div>
          </div>
        )}

        <h2 className="text-2xl font-bold mb-3">How to ask</h2>
        <ol className="space-y-3 mb-8">
          {GENTLE_METHOD.map((step, index) => (
            <li key={step.key} className={card}>
              <h3 className="font-semibold text-white">
                {index + 1}. {step.step}
              </h3>
              <p className="text-slate-300">{step.description}</p>
              {Array.isArray(step.examples) && step.examples[0] && (
                <p className="text-sm text-thread-sand mt-2">Try: &ldquo;{step.examples[0]}&rdquo;</p>
              )}
              {Array.isArray(step.tips) && step.tips[0] && (
                <p className="text-sm text-thread-sand mt-2">{step.tips[0]}</p>
              )}
              {Array.isArray(step.techniques) && step.techniques[0] && (
                <p className="text-sm text-thread-sand mt-2">{step.techniques[0]}</p>
              )}
              {Array.isArray(step.questions) && step.questions[0] && (
                <p className="text-sm text-thread-sand mt-2">Try: &ldquo;{step.questions[0]}&rdquo;</p>
              )}
            </li>
          ))}
        </ol>

        <h2 className="text-2xl font-bold mb-3">If it wanders</h2>
        <div className="grid gap-4 sm:grid-cols-2">
          {NAVIGATION_TIPS.map((situation) => (
            <div key={situation.key} className={card}>
              <h3 className="font-semibold text-white mb-1">{SITUATION_LABELS[situation.key] || situation.key}</h3>
              <ul className="list-disc list-inside text-slate-300 space-y-1">
                {situation.tips.slice(0, 3).map((tip) => (
                  <li key={tip}>{tip}</li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default InterviewKit;
